<template>
  <div>
    <v-overlay :value="loaderOverlay" :absolute="true">
      <v-row justify="center">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-row>
    </v-overlay>

    <v-card outlined class="px-2 w-100 page-card">
      <v-card-title>
        <v-icon large class="pr-2">mdi-account-details</v-icon>
        <h3>{{ $t("projectManagement") }} - {{ $t("permissionsAndAssignments") }}</h3>
      </v-card-title>

      <div class="page-content">
        <div class="page-fixed">
          <v-row no-gutters class="mx-4">
            <v-col>
              <v-autocomplete
                v-model="selectedUser"
                :items="employees"
                :label="$t('selectUserHint')"
                item-text="name"
                color="primary"
                style="width: 80%"
                return-object
                @change="
                  mirroringActive = false;
                  usersDataWithoutCurrentUser = [];
                "
              >
                <template slot="item" slot-scope="data">
                  <template>
                    <v-list>
                      <v-list-item-content name="data.item.name">
                        <v-list-item-title name="data.item.SAPEmpId">{{ data.item.name }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list>
                  </template>
                </template>
                <template v-slot:item="data">
                  <v-list-item-content>
                    <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ getUserTeamsAsString(data.item.teams) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <div class="page-scroll">
              <v-select v-model="selectedTeam" :items="teams" item-text="name" :label="$t('selectDepartmentHint')" color="primary" class="mx-2" />
            </div>
          </v-row>

          <v-row justify="center" class="mt-0" v-if="selectedUser">
            <v-switch class="mt-0 pt-0" :label="$t('mirroringActive')" v-model="mirroringActive"></v-switch>
          </v-row>

          <MirrorPermissions
            v-if="mirroringActive"
            class="mx-4"
            :selectedUser="selectedUser"
            :usersData="usersData"
            :usersDataWithoutCurrentUser="usersDataWithoutCurrentUser"
            @mirroringUpdate="handleMirroringUpdate"
          />
        </div>

        <div class="page-scroll">
          <ProjectStages v-if="selectedUser" :selectedUser="selectedUser" @updatedProject="handleStageUpdate" />
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ProjectManagement",
  route: "ProjectManagement",
  components: {
    ProjectStages: () => import("./ProjectManagement/ProjectStages.vue"),
    MirrorPermissions: () => import("./ProjectManagement/MirrorPermissions.vue")
  },
  computed: {
    ...mapGetters({
      teams: "users/teams"
    })
  },
  data: () => ({
    Users: [],
    userCombobox: [],
    employees: [],
    usersDataWithoutCurrentUser: [],
    selectedTeam: "",
    loaderOverlay: true,
    selectedUser: null,
    selectedActivityTypes: [],
    activityTypes: [],
    usersData: [],
    mirroringActive: false,
    updatedMirroringSettings: {
      mirrorActivities: false,
      mirrorStages: false,
      selectedActivityLevel: 0,
      selectedStageLevel: 0
    }
  }),
  props: {},
  created: async function () {
    await this.getUsers();
    await this.getTeams();
    this.selectedTeam = this.teams[0];
  },
  beforeDestroy: function () {
    this.$emit("close");
  },
  methods: {
    async getUsers() {
      await this.$store
        .dispatch("users/getUsers")
        .then(() => {
          let data = this.$store.getters["users/userList"];
          this.usersData = data.filter((el) => el.name !== "admin");
          this.$forceUpdate();
        })
        .catch((err) => {
          if (err.status != 200) {
            this.$eventHub.$emit("showError", err, "bottom", "right");
          }
        });

      this.loaderOverlay = false;
    },
    async getTeams() {
      await this.$store
        .dispatch("users/getTeams")
        .then(() => {})
        .catch((err) => {
          if (err.response.status != 200) {
            this.$eventHub.$emit("showError", err.response.data, "bottom", "right");
          }
        });
    },
    getUserTeamsAsString(teams) {
      return teams.map((team) => team.Team).join(", ");
    },
    handleMirroringUpdate(data) {
      this.updatedMirroringSettings = data;
    },
    handleStageUpdate({ updatedProject, Projects, selectedStages, activities, plannedCosts, plannedTime, updatedActivities, updatedStages }) {
      this.updatedMirroringSettings.selectedUsers =
        this.updatedMirroringSettings.selectedUsers?.map(
          (user) =>
            new Object({
              SAPEmpId: user.SAPEmpID,
              name: user.name,
              project: updatedProject.AbsEntry
            })
        ) ?? [];

      const payload = {
        updatedProjectID: updatedProject.AbsEntry,
        ProjectList: Projects,
        selectedStages,
        activities,
        SAPEmpID: this.selectedUser.SAPEmpID,
        selectedUserName: this.selectedUser.name,
        mirroring: this.updatedMirroringSettings,
        updatedActivities,
        updatedStages,
        plannedCosts,
        plannedTime
      };

      this.save(payload);
    },
    async save(payload) {
      this.loaderOverlay = true;

      await this.$store.dispatch("user/setProjectPermissionForUser", payload).catch((err) => {
        this.loaderOverlay = false;
        console.error(err);
        this.$eventHub.$emit("showError", err, "bottom", "right");
      });

      if (payload.activities.length === 0) {
        this.$eventHub.$emit("showSuccess", `${this.$t("successfullySetProjectPermissions")}: ${payload.selectedUserName}`, "bottom", "right");
        return (this.loaderOverlay = false);
      }

      this.$store
        .dispatch("user/setActivityPermissionForUser", payload)
        .then((_) => {
          if (payload.mirroring.selectedUsers.length === 0) {
            this.$eventHub.$emit("showSuccess", `${this.$t("successfullySetProjectPermissions")}: ${payload.selectedUserName}`, "bottom", "right");
          } else {
            let names = payload.mirroring.selectedUsers.map((user) => user.name).join(", ");
            this.$eventHub.$emit("showSuccess", `${this.$t("successfullySetProjectPermissions")}: ${payload.selectedUserName}, ${names}`, "bottom", "right");
          }
          this.loaderOverlay = false;
        })
        .catch((err) => {
          this.loaderOverlay = false;
          console.error(err);
          if (err.response.status != 200) {
            this.$eventHub.$emit("showError", err.response.data, "bottom", "right");
          }
        });
    }
  },
  watch: {
    userProjectPermissionsDialogModel: function (val) {
      if (!val) this.$destroy();
    },
    selectedTeam() {
      if (this.selectedTeam === this.$t("all")) this.employees = [...this.usersData];
      else {
        this.employees = this.usersData.filter((employee) => employee.teams.some((team) => team.Team === this.selectedTeam));
      }
      if (!this.selectedUser || this.employees.find((employee) => employee.name === this.selectedUser.name)) return;
      this.selectedUser = null;
      this.mirroringActive = false;
    }
  }
};
</script>

<style scoped>
.page-card {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
}
.page-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.page-fixed {
  flex: 0 0 auto;
}
.page-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
</style>
<style scoped>
.page-card {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
}
.page-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.page-fixed {
  flex: 0 0 auto;
}
.page-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
</style>
